// components/CostSummary.tsx
// NOTE: Update frontend/src/components/__tests__/CostSummary.test.tsx and run
// `../scripts/test-frontend.sh CostSummary` when modifying this component.
import React from 'react'
import { Tooltip } from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { estimateUSD, formatUSD, shouldShowDollarEquivalents } from '../utils/costUtils'

interface CostSummaryProps {
  creditBalance: number | null | undefined
  baseCost?: number | null
  discountedCost?: number
  planId?: string
  tooltipText?: string
  containerStyle?: React.CSSProperties
  headerActionNode?: React.ReactNode
  afterCostNode?: React.ReactNode // e.g., a discount tag
}

export const CostSummary: React.FC<CostSummaryProps> = ({
  creditBalance,
  baseCost,
  discountedCost,
  planId,
  tooltipText = 'Credits are charged per visible word; HTML/XML tags do not count toward usage.',
  containerStyle,
  headerActionNode,
  afterCostNode,
}) => {
  const [approxUsd, setApproxUsd] = React.useState<string | null>(null)
  const hasNumericBaseCost = typeof baseCost === 'number' && Number.isFinite(baseCost)
  const hasNumericDiscount = typeof discountedCost === 'number' && Number.isFinite(discountedCost)
  const formatCredits = React.useCallback((value: number) => {
    const rounded = Math.max(0, Math.round(value))
    return new Intl.NumberFormat('en-US').format(rounded)
  }, [])
  const effective = hasNumericBaseCost
    ? hasNumericDiscount
      ? discountedCost!
      : baseCost!
    : null

  React.useEffect(() => {
    let cancelled = false
    const run = async () => {
      try {
        if (!shouldShowDollarEquivalents()) { setApproxUsd(null); return }
        if (!effective || !isFinite(effective) || effective <= 0) { setApproxUsd(null); return }
        const usd = await estimateUSD(effective, planId)
        if (!cancelled) setApproxUsd(formatUSD(usd))
      } catch { if (!cancelled) setApproxUsd(null) }
    }
    run()
    return () => { cancelled = true }
  }, [effective, planId])

  return (
    <div
      style={{
        background: 'var(--surface-info)',
        padding: 15,
        borderRadius: 6,
        marginBottom: 15,
        ...(containerStyle || {}),
      }}
    >
      <div
        style={{
          marginBottom: 8,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <div>
          <strong>Credit Balance: </strong>
          <span>{creditBalance == null ? 'Loading...' : formatCredits(creditBalance)}</span>
        </div>
        {headerActionNode}
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <strong>Credit Cost: </strong>
        <span style={{ marginLeft: 5, marginRight: 5 }}>
          {hasNumericBaseCost ? (
            hasNumericDiscount && discountedCost !== baseCost ? (
              <>
                <span style={{ textDecoration: 'line-through', color: 'var(--text-secondary)' }}>
                  {formatCredits(baseCost)}
                </span>
                <span style={{ marginLeft: 8, color: 'var(--text-success)', fontWeight: 'bold' }}>
                  {formatCredits(discountedCost)}
                </span>
              </>
            ) : (
              formatCredits(baseCost)
            )
          ) : (
            <span style={{ color: 'var(--text-secondary)' }}>Calculating…</span>
          )}
        </span>
        <Tooltip title={tooltipText}>
          <QuestionCircleOutlined className="modal-token-icon modal-token-icon--interactive" />
        </Tooltip>
        {afterCostNode}
        {approxUsd && (
          <Tooltip title="Approximate $ based on plan; this is an estimate.">
            <span style={{ marginLeft: 8, color: 'var(--text-secondary)' }}>{approxUsd}</span>
          </Tooltip>
        )}
      </div>
    </div>
  )
}

export default CostSummary
